export type ForfeoOperationsRunbook = {
  id: string;
  domain: string;
  title: string;
  objective: string;
  owner: "explore" | "giftshop" | "shared";
  caseCategory: string;
  steps: readonly string[];
  stopConditions: readonly string[];
  evidence: readonly string[];
  financialBoundary: string;
};

export const FORFEO_OPERATIONS_RUNBOOKS: readonly ForfeoOperationsRunbook[] = [
  {
    id: "incident-triage",
    domain: "incidents",
    title: "Triage d’un incident",
    objective: "Qualifier rapidement l’impact, le périmètre et le propriétaire sans modifier les données métier.",
    owner: "shared",
    caseCategory: "incident",
    steps: [
      "Créer un dossier idempotent avec l’heure, le système source et la référence concernée.",
      "Consigner les symptômes observables et les agrégats du modèle de lecture.",
      "Évaluer la priorité selon l’impact utilisateur et le risque financier.",
      "Assigner le propriétaire du système source puis passer le dossier en traitement.",
      "Documenter la validation de reprise avant la résolution.",
    ],
    stopConditions: [
      "Identité administrateur non vérifiée.",
      "Périmètre ou système source impossible à confirmer.",
      "Action susceptible de modifier un paiement, un remboursement ou une commande.",
    ],
    evidence: ["Horodatage", "Référence source", "Impact", "Observations", "Validation de reprise"],
    financialBoundary: "Toute action financière reste dans Giftshop et dans un chemin serveur autorisé.",
  },
  {
    id: "error-correlation",
    domain: "error_tracking",
    title: "Corrélation des erreurs",
    objective: "Relier les erreurs répétées à un dossier unique et conserver une chronologie exploitable.",
    owner: "shared",
    caseCategory: "incident",
    steps: [
      "Comparer la fenêtre temporelle, la route, le système et la référence métier.",
      "Rattacher les observations au dossier existant quand la cause probable est commune.",
      "Ajouter une note factuelle; ne jamais copier de secret, jeton ou donnée complète de paiement.",
      "Escalader la priorité si l’impact ou la fréquence augmente.",
    ],
    stopConditions: ["Présence possible d’un secret", "Données personnelles non nécessaires", "Cause non corrélée"],
    evidence: ["Fenêtre temporelle", "Route", "Code d’erreur", "Nombre agrégé", "Corrélation"],
    financialBoundary: "L’observation ne déclenche ni capture, ni annulation, ni remboursement.",
  },
  {
    id: "queue-recovery",
    domain: "operational_queues",
    title: "Reprise d’une file opérationnelle",
    objective: "Préparer une reprise contrôlée sans rejouer aveuglément un effet métier.",
    owner: "shared",
    caseCategory: "queue",
    steps: [
      "Mesurer le volume en attente, en échec et l’âge du plus ancien élément.",
      "Vérifier la clé d’idempotence et l’état canonique dans le système propriétaire.",
      "Isoler les éléments ambigus dans un dossier plutôt que de les rejouer.",
      "Faire autoriser toute reprise serveur par le propriétaire opérationnel.",
      "Après reprise autorisée, confirmer les effets et fermer uniquement quand la file est stable.",
    ],
    stopConditions: ["Idempotence non démontrée", "Autorité source indisponible", "Effet financier possible non autorisé"],
    evidence: ["Comptes avant/après", "Clé d’idempotence", "Autorisation", "Résultat de reprise"],
    financialBoundary: "La console navigateur décrit et suit la reprise; elle ne rejoue aucun événement.",
  },
  {
    id: "assisted-refund",
    domain: "assisted_refunds",
    title: "Remboursement assisté",
    objective: "Rassembler les preuves et suivre la demande sans exécuter le remboursement depuis Explore.",
    owner: "giftshop",
    caseCategory: "refund",
    steps: [
      "Créer un dossier avec la commande Giftshop et le motif fourni.",
      "Vérifier en lecture l’état canonique du paiement et l’absence de remboursement déjà finalisé.",
      "Consigner le montant demandé sans donnée de carte ni secret Stripe.",
      "Transmettre l’exécution au chemin serveur Giftshop explicitement autorisé.",
      "Observer l’événement canonique, l’outbox et la synchronisation Explore avant résolution.",
    ],
    stopConditions: ["Commande introuvable", "Montant incohérent", "Remboursement déjà traité", "Autorisation absente"],
    evidence: ["Commande", "Motif", "Montant attendu", "Événement Giftshop", "Synchronisation Explore"],
    financialBoundary: "Giftshop demeure l’unique autorité du remboursement; cette interface n’appelle aucune fonction financière.",
  },
  {
    id: "dispute-preservation",
    domain: "disputes",
    title: "Préservation d’un litige",
    objective: "Conserver les faits, les décisions et les références sans altérer la transaction d’origine.",
    owner: "giftshop",
    caseCategory: "dispute",
    steps: [
      "Créer un dossier distinct avec la référence du litige et la commande liée.",
      "Documenter uniquement les faits vérifiables et les échéances.",
      "Préserver les références aux événements financiers canoniques Giftshop.",
      "Ajouter chaque décision comme note interne auditée.",
      "Résoudre le dossier après confirmation du système financier propriétaire.",
    ],
    stopConditions: ["Référence non vérifiable", "Demande de modification directe de transaction", "Donnée sensible inutile"],
    evidence: ["Référence du litige", "Commande", "Échéance", "Décisions", "Résultat canonique"],
    financialBoundary: "Aucune décision enregistrée ici ne vaut mutation financière.",
  },
  {
    id: "internal-notes",
    domain: "internal_notes",
    title: "Notes internes auditées",
    objective: "Conserver une chronologie factuelle réservée aux administrateurs.",
    owner: "explore",
    caseCategory: "incident",
    steps: [
      "Écrire des faits brefs et attribuables.",
      "Exclure secrets, jetons, données de carte et données personnelles non nécessaires.",
      "Ajouter une nouvelle note pour corriger une information; ne pas réécrire l’historique.",
      "Relier toute décision à une observation ou une référence vérifiable.",
    ],
    stopConditions: ["Secret détecté", "Donnée excessive", "Auteur non administrateur"],
    evidence: ["Auteur", "Horodatage", "Fait", "Référence"],
    financialBoundary: "Les notes sont informatives et ne déclenchent aucun effet métier.",
  },
  {
    id: "recovery-validation",
    domain: "recovery_procedures",
    title: "Validation d’une reprise",
    objective: "Fermer un dossier seulement après vérification mesurable du service restauré.",
    owner: "shared",
    caseCategory: "recovery",
    steps: [
      "Définir le signal de santé attendu avant toute intervention.",
      "Noter l’intervention autorisée réalisée dans le système propriétaire.",
      "Comparer les agrégats et erreurs avant/après.",
      "Surveiller une fenêtre suffisante et documenter le résultat.",
      "Résoudre puis fermer le dossier avec les preuves de stabilité.",
    ],
    stopConditions: ["Signal de santé indéfini", "Régression observée", "Données contradictoires"],
    evidence: ["Signal attendu", "Avant/après", "Fenêtre d’observation", "Conclusion"],
    financialBoundary: "Une reprise financière éventuelle reste une opération serveur Giftshop distincte et autorisée.",
  },
] as const;

export function getForfeoRunbook(runbookId: string) {
  return FORFEO_OPERATIONS_RUNBOOKS.find((runbook) => runbook.id === runbookId) || null;
}
